import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkoutCard = ({ workout, onFavoriteToggle }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'bg-success/10 text-success border-success/20',
      intermediate: 'bg-warning/10 text-warning border-warning/20',
      advanced: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[level?.toLowerCase()] || colors?.beginner;
  };

  const handleStartWorkout = () => {
    navigate('/active-workout', { state: { workout } });
  };

  return (
    <div className="group bg-card border border-border rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-smooth hover-lift">
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        )}
        <Image
          src={workout?.image}
          alt={workout?.imageAlt}
          className={`w-full h-full object-cover transition-smooth group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onFavoriteToggle(workout?.id);
            }}
            className="flex items-center justify-center w-10 h-10 bg-card/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-card transition-smooth press-scale"
            aria-label={workout?.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Icon
              name="Heart"
              size={20}
              color={workout?.isFavorite ? 'var(--color-error)' : 'var(--color-foreground)'}
              fill={workout?.isFavorite ? 'var(--color-error)' : 'none'}
              strokeWidth={2}
            />
          </button>
          {workout?.isNew && (
            <div className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-bold caption">
              NEW
            </div>
          )}
        </div>
        <div className="absolute bottom-3 left-3">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold border caption ${getDifficultyColor(workout?.difficulty)}`}>
            {workout?.difficulty}
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground line-clamp-2 flex-1">
            {workout?.title}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
            <span className="text-sm font-medium data-text text-foreground">{workout?.rating}</span>
          </div>
        </div>

        <p className="text-sm md:text-base text-muted-foreground line-clamp-2 mb-4">
          {workout?.description}
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} color="var(--color-primary)" />
            <span className="whitespace-nowrap">{workout?.duration}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Dumbbell" size={16} color="var(--color-primary)" />
            <span className="whitespace-nowrap">{workout?.exercises} exercises</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Flame" size={16} color="var(--color-accent)" />
            <span className="whitespace-nowrap">{workout?.calories} cal</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {workout?.equipment?.slice(0, 3)?.map((item, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs caption"
            >
              {item}
            </span>
          ))}
          {workout?.equipment?.length > 3 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs caption">
              +{workout?.equipment?.length - 3} more
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => workout?.onPreview(workout)}
            className="flex-1"
          >
            Preview
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Play"
            iconPosition="left"
            onClick={handleStartWorkout}
            className="flex-1"
          >
            Start
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutCard;